import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { FiDollarSign, FiPackage, FiCheckCircle, FiAlertTriangle } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import StatusChip from "../../../Common/StatusChip";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import useReportData from "../../useReportData";

const SiteInventoryValuationReport = () => {
  const gridRef = useRef(null);
  const [data] = useReportData("StockValuation");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI metrics
  const kpis = useMemo(() => {
    const totalValuation = data.reduce((s, r) => s + (r.totalValuation || 0), 0);
    const totalUnitsOnHand = data.reduce((s, r) => s + (r.balanceQty || 0), 0);
    const criticalOrLowCount = data.filter(
      (r) => r.status === "Critical" || r.status === "Low",
    ).length;
    const healthyCount = data.filter((r) => r.status === "Healthy").length;

    return {
      totalValuation,
      totalUnitsOnHand,
      totalItems: data.length,
      criticalOrLowCount,
      healthyCount,
    };
  }, [data]);

  // Unique locations for filter
  const uniqueLocations = useMemo(() => {
    return Array.from(new Set(data.map((d) => d.location).filter(Boolean)));
  }, [data]);

  // Column definitions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Project Site / Store Location",
        field: "location",
        colId: "location",
        flex: 1.6,
        minWidth: 200,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 600, color: "var(--color-primary, #0b6462)" }}>{p.value}</span>
        ),
      },
      {
        headerName: "Item Code / SKU",
        field: "itemCode",
        colId: "itemCode",
        flex: 1.2,
        minWidth: 140,
        cellRenderer: (p) => (
          <span
            style={{
              fontFamily: "monospace",
              background: "#f1f5f9",
              padding: "2px 6px",
              borderRadius: "4px",
              fontSize: "11px",
              fontWeight: 600,
              color: "#334155",
            }}
          >
            {p.value}
          </span>
        ),
      },
      {
        headerName: "Item Name & Technical Specification",
        field: "itemName",
        colId: "itemName",
        flex: 2,
        minWidth: 230,
        cellRenderer: (p) => <span style={{ fontWeight: 600, color: "#1e293b" }}>{p.value}</span>,
      },
      {
        headerName: "UOM",
        field: "uom",
        colId: "uom",
        flex: 0.9,
        minWidth: 90,
      },
      {
        headerName: "Opening Balance",
        field: "openingQty",
        colId: "openingQty",
        flex: 1.1,
        minWidth: 130,
        valueFormatter: (p) =>
          p.value !== undefined && p.value !== null ? Number(p.value).toLocaleString() : "—",
      },
      {
        headerName: "Received Qty (GRNs)",
        field: "receivedQty",
        colId: "receivedQty",
        flex: 1.2,
        minWidth: 140,
        cellRenderer: (p) => (
          <span style={{ color: "#16a34a", fontWeight: 600 }}>
            +{p.value !== undefined ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Issued Qty (MINs)",
        field: "issuedQty",
        colId: "issuedQty",
        flex: 1.2,
        minWidth: 140,
        cellRenderer: (p) => (
          <span style={{ color: "#dc2626", fontWeight: 600 }}>
            -{p.value !== undefined ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Current Balance Qty",
        field: "balanceQty",
        colId: "balanceQty",
        flex: 1.3,
        minWidth: 140,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 700, color: "#0f172a" }}>
            {p.value !== undefined ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Weighted Avg Unit Cost (PKR)",
        field: "unitCost",
        colId: "unitCost",
        flex: 1.4,
        minWidth: 160,
        valueFormatter: (p) => (p.value ? `PKR ${Number(p.value).toLocaleString()}` : "—"),
      },
      {
        headerName: "Total Stock Valuation (PKR)",
        field: "totalValuation",
        colId: "totalValuation",
        flex: 1.5,
        minWidth: 170,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 700, color: "var(--color-primary, #0b6462)" }}>
            {p.value ? `PKR ${Number(p.value).toLocaleString()}` : "—"}
          </span>
        ),
      },
      {
        headerName: "Stock Status",
        field: "status",
        colId: "status",
        flex: 1,
        minWidth: 110,
        cellRenderer: (p) => {
          const val = p.value || "Healthy";
          let type = "success";
          if (val === "Critical") {
            type = "danger";
          } else if (val === "Low") {
            type = "warning";
          }
          return <StatusChip status={val} type={type} />;
        },
      },
    ];
  }, []);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    [],
  );

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const q = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (item.location && item.location.toLowerCase().includes(q)) ||
        (item.itemCode && item.itemCode.toLowerCase().includes(q)) ||
        (item.itemName && item.itemName.toLowerCase().includes(q));

      const matchesStatus =
        statusFilter === "all" || item.status?.toLowerCase() === statusFilter.toLowerCase();

      const matchesLocation =
        locationFilter === "all" || item.location?.toLowerCase() === locationFilter.toLowerCase();

      return matchesSearch && matchesStatus && matchesLocation;
    });
  }, [data, searchTerm, statusFilter, locationFilter]);

  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({ ...prev, [colId]: !isCurrentlyVisible }));
    },
    [columnVisibility],
  );

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `Site_Inventory_Stock_Valuation_${new Date().toISOString().slice(0, 10)}.csv`,
      });
      toast.success("Stock balance and valuation report exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOTAL STOCK VALUATION"
            value={`PKR ${(kpis.totalValuation / 1000000).toFixed(2)}M`}
            subtitle={`PKR ${kpis.totalValuation.toLocaleString()} across stores`}
            icon={FiDollarSign}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="MATERIALS MONITORED"
            value={`${kpis.totalItems} SKUs`}
            subtitle={`${uniqueLocations.length} active site store locations`}
            icon={FiPackage}
            color="#0284c7"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="OPTIMAL STOCK LEVELS"
            value={`${kpis.healthyCount} Items`}
            subtitle="Healthy inventory coverage"
            icon={FiCheckCircle}
            color="#16a34a"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="LOW / CRITICAL STOCK ALERT"
            value={`${kpis.criticalOrLowCount} Items`}
            subtitle="Requires replenishment requisition"
            icon={FiAlertTriangle}
            color="#dc2626"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (General Journal style) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="Stock Balance & Valuation"
            >
              Stock Balance & Valuation
            </div>
            <SearchFilter value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />

            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            <div className="d-flex align-items-center" style={{ maxWidth: "220px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                >
                  <option value="all">All Store Locations</option>
                  {uniqueLocations.map((loc) => (
                    <option key={loc} value={loc}>
                      {loc}
                    </option>
                  ))}
                </select>
                <label>Store Location</label>
              </div>
            </div>

            <div className="d-flex align-items-center" style={{ maxWidth: "180px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="healthy">Healthy</option>
                  <option value="low">Low Stock</option>
                  <option value="critical">Critical</option>
                </select>
                <label>Status</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiteInventoryValuationReport;
